// All coordinates are stored as ratios (0-1) relative to canvas dimensions
// This ensures diagrams display correctly across different screen sizes
interface ArrowData {
  startX: number;  // ratio 0-1
  startY: number;  // ratio 0-1
  endX: number;    // ratio 0-1
  endY: number;    // ratio 0-1
  color: string;
  style: string;
  ctrl?: { x: number; y: number } | null;  // ratios 0-1
  bounce?: { x: number; y: number };        // ratios 0-1
}

interface PlayerData {
  x: number;  // ratio 0-1
  y: number;  // ratio 0-1
  color: string;
  label: string;
}

interface CanvasState {
  players: PlayerData[];
  arrows: ArrowData[];
}

interface Slide {
  id: string;
  description: string;
  canvasState: CanvasState;
}

interface AudioSegment {
  id: string;
  url: string;
  duration: number;
  createdAt: number;
}

interface DiagramState {
  description: string;
  slides: Slide[];
  currentSlideIndex: number;
  audioSegments: AudioSegment[];
}

type MessageHandler = (message: unknown) => void;

export class SyncClient {
  private socket: WebSocket | null = null;
  private diagramId: string;
  private clientId: string | null = null;
  private messageHandlers: Map<string, MessageHandler[]> = new Map();
  private reconnectAttempts = 0;
  private maxReconnectAttempts = 5;
  private reconnectDelay = 1000;
  private isConnected = false;
  private pendingMessages: string[] = [];

  constructor(diagramId: string) {
    this.diagramId = diagramId;
  }

  connect(): Promise<DiagramState | null> {
    return new Promise((resolve) => {
      const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      const wsUrl = `${protocol}//${window.location.host}/ws/d/${this.diagramId}`;

      console.log('Connecting to:', wsUrl);
      this.socket = new WebSocket(wsUrl);

      this.socket.onopen = () => {
        console.log('WebSocket connected');
        this.isConnected = true;
        this.reconnectAttempts = 0;

        // Send any pending messages
        this.pendingMessages.forEach(msg => this.socket?.send(msg));
        this.pendingMessages = [];
      };

      this.socket.onmessage = (event) => {
        try {
          const message = JSON.parse(event.data);

          // Handle initial sync
          if (message.type === 'sync') {
            this.clientId = message.clientId;
            resolve(message.state);
            return;
          }

          // Dispatch to handlers
          const handlers = this.messageHandlers.get(message.type) || [];
          handlers.forEach(handler => handler(message));
        } catch (error) {
          console.error('Failed to parse message:', error);
        }
      };

      this.socket.onclose = () => {
        console.log('WebSocket closed');
        this.isConnected = false;
        this.attemptReconnect();
      };

      this.socket.onerror = (error) => {
        console.error('WebSocket error:', error);
        resolve(null);
      };

      // Timeout for initial connection
      setTimeout(() => {
        if (!this.isConnected) {
          resolve(null);
        }
      }, 5000);
    });
  }

  private attemptReconnect(): void {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.log('Max reconnect attempts reached');
      return;
    }

    this.reconnectAttempts++;
    const delay = this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1);

    console.log(`Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts})`);

    setTimeout(() => {
      this.connect();
    }, delay);
  }

  on(messageType: string, handler: MessageHandler): void {
    const handlers = this.messageHandlers.get(messageType) || [];
    handlers.push(handler);
    this.messageHandlers.set(messageType, handlers);
  }

  off(messageType: string, handler: MessageHandler): void {
    const handlers = this.messageHandlers.get(messageType) || [];
    const index = handlers.indexOf(handler);
    if (index !== -1) {
      handlers.splice(index, 1);
      this.messageHandlers.set(messageType, handlers);
    }
  }

  private send(message: object): void {
    const data = JSON.stringify({ ...message, from: this.clientId });

    if (this.isConnected && this.socket?.readyState === WebSocket.OPEN) {
      this.socket.send(data);
    } else {
      // Queue message for when connection is established
      this.pendingMessages.push(data);
    }
  }

  // Diagram-specific methods
  sendSlideUpdate(slideIndex: number, canvasState: CanvasState): void {
    this.send({
      type: 'slide-update',
      slideIndex,
      canvasState,
    });
  }

  sendSlideAdd(slide: Slide, insertAfterIndex: number): void {
    this.send({
      type: 'slide-add',
      slide,
      insertAfterIndex,
    });
  }

  sendSlideDelete(slideIndex: number): void {
    this.send({
      type: 'slide-delete',
      slideIndex,
    });
  }

  sendSlideNavigate(slideIndex: number): void {
    this.send({
      type: 'slide-navigate',
      slideIndex,
    });
  }

  sendDescriptionUpdate(description: string): void {
    this.send({
      type: 'description-update',
      description,
    });
  }

  sendFullSync(state: DiagramState): void {
    this.send({
      type: 'full-sync',
      state,
    });
  }

  sendAudioSegmentAdd(segment: AudioSegment): void {
    this.send({
      type: 'audio-segment-add',
      segment,
    });
  }

  sendAudioSegmentDelete(segmentId: string): void {
    this.send({
      type: 'audio-segment-delete',
      segmentId,
    });
  }

  disconnect(): void {
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
    this.isConnected = false;
    this.messageHandlers.clear();
  }

  get connected(): boolean {
    return this.isConnected;
  }

  get id(): string | null {
    return this.clientId;
  }
}
